/**
 * Top Projects Table
 *
 * Table showing top projects by revenue.
 */

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Link } from 'react-router-dom';
import type { TopProject } from '../types';

interface TopProjectsTableProps {
  data?: TopProject[];
  loading?: boolean;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency: 'CAD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

const STATUS_STYLES: Record<string, string> = {
  confirmed: 'bg-blue-100 text-blue-800',
  scheduled: 'bg-amber-100 text-amber-800',
  invoiced: 'bg-green-100 text-green-800',
  sent_to_lab: 'bg-amber-100 text-amber-800',
  shipped: 'bg-purple-100 text-purple-800',
  delivered: 'bg-green-100 text-green-800',
  roadblock: 'bg-red-100 text-red-800',
};

const STATUS_LABELS: Record<string, string> = {
  confirmed: 'Confirmed',
  scheduled: 'Scheduled',
  invoiced: 'Invoiced',
  sent_to_lab: 'Sent to Lab',
  shipped: 'Shipped',
  delivered: 'Delivered',
  roadblock: 'Roadblock',
};

const TYPE_LABELS: Record<string, string> = {
  installation: 'Installation',
  shipping: 'Shipping',
  calibration: 'Calibration',
  replacement: 'Replacement',
  quote: 'Quote',
};

export function TopProjectsTable({ data, loading }: TopProjectsTableProps) {
  if (loading) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base font-medium">Top Projects by Value</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div key={i} className="animate-pulse flex gap-4">
                <div className="h-5 bg-muted rounded w-20" />
                <div className="h-5 bg-muted rounded flex-1" />
                <div className="h-5 bg-muted rounded w-16" />
                <div className="h-5 bg-muted rounded w-20" />
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const hasData = data && data.length > 0;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-medium">Top Projects by Value</CardTitle>
      </CardHeader>
      <CardContent className="p-0">
        {hasData ? (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[100px]">Project</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Value</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((project) => (
                <TableRow key={project.id}>
                  <TableCell className="font-mono text-xs">
                    <Link
                      to={`/operations/projects?selected=${project.id}`}
                      className="text-blue-600 hover:underline"
                    >
                      {project.project_number}
                    </Link>
                  </TableCell>
                  <TableCell className="font-medium truncate max-w-[150px]">
                    {project.project_name || '-'}
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {TYPE_LABELS[project.type] || project.type}
                  </TableCell>
                  <TableCell>
                    <Badge className={STATUS_STYLES[project.status] || 'bg-gray-100 text-gray-800'}>
                      {STATUS_LABELS[project.status] || project.status}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCurrency(project.amount)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <div className="flex items-center justify-center h-32 text-muted-foreground text-sm">
            No project data available
          </div>
        )}
      </CardContent>
    </Card>
  );
}
